import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Flame, Shield, BadgeCheck, CheckCircle2, XCircle, Save, Upload, X } from "lucide-react";
import * as XLSX from "xlsx";
import { photoUrl } from "../lib/photos";
import { FlatmateWaIntents } from "./FlatmateWaIntents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useApi = (token) => {
  const h = { Authorization: `Bearer ${token}` };
  return {
    get: (p) => axios.get(`${API}${p}`, { headers: h }),
    post: (p, b = {}) => axios.post(`${API}${p}`, b, { headers: h }),
  };
};

export function AdminFlatmate({ token }) {
  const api = useApi(token);
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [listings, setListings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [verifs, setVerifs] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [settings, setSettings] = useState(null);
  const [contactReqs, setContactReqs] = useState([]);
  const [partners, setPartners] = useState([]);
  const [outreach, setOutreach] = useState({ listings: [] });
  const [drafts, setDrafts] = useState([]);
  const [aiExtract, setAiExtract] = useState({
    image_urls: "",
    page_url: "",
    extra_context: "",
    publish: true,     // admin default = publish live
    busy: false,
    last: null,
    files: [],         // File[] up to 2
  });
  const [editing, setEditing] = useState(null); // {id, patch}

  const refresh = async () => {
    try {
      const [d, l, p, j, v, u, s, cr, pr, ot, dr] = await Promise.all([
        api.get("/admin/flatmate/dashboard"),
        api.get("/admin/flatmate/listings"),
        api.get("/admin/flatmate/payments"),
        api.get("/admin/flatmate/journeys"),
        api.get("/admin/flatmate/tenant-verifications"),
        api.get("/admin/flatmate/users"),
        api.get("/admin/flatmate/settings"),
        api.get("/admin/flatmate/contact-requests"),
        api.get("/admin/partners"),
        api.get("/admin/flatmate/outreach"),
        api.get("/admin/flatmate/drafts?status_filter=draft"),
      ]);
      setDash(d.data);
      setListings(l.data.listings || []);
      setPayments(p.data.payments || []);
      setJourneys(j.data.journeys || []);
      setVerifs(v.data.verifications || []);
      setProfiles(u.data.profiles || []);
      setSettings(s.data.settings);
      setContactReqs(cr.data.requests || []);
      setPartners(pr.data.partners || []);
      setOutreach(ot.data || { listings: [] });
      setDrafts(dr.data.drafts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const runAiExtract = async () => {
    const image_urls = aiExtract.image_urls.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
    const hasFiles = (aiExtract.files || []).length > 0;
    if (!image_urls.length && !aiExtract.page_url && !hasFiles) {
      toast.error("Attach up to 2 images, paste URLs, or a web page URL");
      return;
    }
    setAiExtract((x) => ({ ...x, busy: true }));
    try {
      let r;
      if (hasFiles) {
        // Multipart one-shot upload + extract
        const form = new FormData();
        (aiExtract.files || []).slice(0, 2).forEach((f) => form.append("files", f));
        if (aiExtract.page_url) form.append("page_url", aiExtract.page_url);
        if (aiExtract.extra_context) form.append("extra_context", aiExtract.extra_context);
        form.append("publish", String(aiExtract.publish));
        r = await axios.post(`${API}/admin/flatmate/auto-extract-files`, form, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
      } else {
        r = await api.post("/admin/flatmate/auto-extract", {
          image_urls,
          page_url: aiExtract.page_url || null,
          extra_context: aiExtract.extra_context || null,
          publish: aiExtract.publish,
        });
      }
      const d = r.data;
      if (d.credit_charged) {
        toast.success(`Draft created · ${d.extracted_fields_count} fields · ${d.listing?.listing_code}`);
      } else {
        toast(`Extraction returned only ${d.extracted_fields_count} field(s). No credit charged — please retry with a clearer image / link.`,
              { icon: "🎁", duration: 6000 });
      }
      setAiExtract((x) => ({ ...x, busy: false, last: d,
                            image_urls: "", page_url: "", extra_context: "", files: [] }));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed — no credit charged");
      setAiExtract((x) => ({ ...x, busy: false }));
    }
  };
  const onPickFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) =>
      ["image/jpeg", "image/png", "image/webp"].includes(f.type));
    if (files.length === 0) {
      toast.error("Only JPG, PNG, WebP allowed");
      return;
    }
    setAiExtract((x) => ({ ...x, files: [...(x.files || []), ...files].slice(0, 2) }));
  };
  const removeFile = (idx) => setAiExtract((x) => ({ ...x, files: x.files.filter((_, i) => i !== idx) }));
  const patchListing = async (lid, patch) => {
    try {
      const r = await axios.patch(`${API}/admin/flatmate/listings/${lid}`, { patch }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Saved");
      setEditing(null);
      refresh();
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };
  const publishListing = async (lid) => { await api.post(`/admin/flatmate/listings/${lid}/publish`); toast.success("Published — live now"); refresh(); };
  const unpublishListing = async (lid) => { await api.post(`/admin/flatmate/listings/${lid}/unpublish`); toast.success("Unpublished"); refresh(); };
  const rejectListing = async (lid) => { await api.post(`/admin/flatmate/listings/${lid}/reject`); toast.success("Rejected"); refresh(); };
  const deleteListing = async (lid) => {
    if (!window.confirm("Delete this listing permanently?")) return;
    await axios.delete(`${API}/admin/flatmate/listings/${lid}`, { headers: { Authorization: `Bearer ${token}` } });
    toast.success("Deleted"); refresh();
  };

  const flag = async (lid, patch) => {
    await api.post(`/admin/flatmate/listings/${lid}/flag`, patch);
    toast.success("Updated");
    refresh();
  };
  const payAction = async (pid, action) => {
    await api.post(`/admin/flatmate/payments/${pid}/${action}`);
    toast.success(action === "approve" ? "Approved & credited" : "Rejected");
    refresh();
  };
  const verifyUser = async (uid, verified_flatmate) => {
    await api.post(`/admin/flatmate/users/verify`, { user_id: uid, verified_flatmate });
    toast.success("Updated");
    refresh();
  };
  const tvMark = async (tid, status) => {
    await api.post(`/admin/flatmate/tenant-verifications/${tid}/mark`, { status });
    toast.success("Updated");
    refresh();
  };

  const saveSettings = async () => {
    await api.post("/admin/flatmate/settings", settings);
    toast.success("Settings saved");
  };

  const tabs = [
    { k: "dashboard", n: "Dashboard" },
    { k: "aiextract", n: `✨ AI Auto-Extract` },
    { k: "drafts", n: `Drafts (${drafts.length})` },
    { k: "listings", n: `Listings (${listings.length})` },
    { k: "free", n: "Free Listing +" },
    { k: "requests", n: `Contact Requests (${contactReqs.filter(r => r.status === "pending").length})` },
    { k: "outreach", n: `Owner Outreach (${outreach.listings.length})` },
    { k: "partners", n: `Partners (${partners.length})` },
    { k: "journeys", n: `₹99 Journeys (${journeys.length})` },
    { k: "payments", n: `Payments (${payments.filter((p) => p.status === "pending").length})` },
    { k: "verifs", n: `Tenant Verify (${verifs.filter((v) => v.status === "pending").length})` },
    { k: "users", n: `Flatmate Users (${profiles.length})` },
    { k: "settings", n: "Settings" },
  ];

  return (
    <div className="space-y-4" data-testid="admin-flatmate">
      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
                  data-testid={`fm-tab-${t.k}`}
                  className={`chip ${tab === t.k ? "bg-emerald-50 border-emerald-500 text-emerald-700" : ""}`}>
            {t.n}
          </button>
        ))}
      </div>

      {tab === "dashboard" && dash && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(dash).map(([k, v]) => (
            <div key={k} className="rounded-2xl border border-black/10 bg-white p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">{k.replace(/_/g, " ")}</div>
              <div className="font-display text-2xl mt-1" style={{ color: "var(--primary)" }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "aiextract" && (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="ai-extract-panel">
          <div className="rounded-2xl border p-5 bg-white" style={{ borderColor: "#00604A22" }}>
            <div className="text-xs uppercase tracking-widest" style={{ color: "#667085" }}>AI Auto-Extract</div>
            <h3 className="mt-1 font-display text-2xl" style={{ color: "#00604A" }}>
              Paste image URLs, Facebook/Instagram/WhatsApp link → get a draft listing.
            </h3>
            <p className="text-xs mt-1" style={{ color: "#667085" }}>
              Powered by Gemini vision. Max 5 images per run · we keep the first 2 on the listing (JPG · PNG · WebP).
              <b style={{ color: "#00604A" }}> No credit charged if the extraction misses required fields.</b>
            </p>

            {/* File drop-zone — up to 2 files (JPG / PNG / WebP) */}
            <div className="mt-4" data-testid="ai-file-dropzone">
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>Upload up to 2 property/deal images</span>
              <label className="mt-1 flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-5 cursor-pointer hover:bg-neutral-50 transition"
                     style={{ borderColor: "#00604A55" }}>
                <input data-testid="ai-file-input" type="file" accept="image/jpeg,image/png,image/webp"
                       multiple className="hidden"
                       onChange={(e) => onPickFiles(e.target.files)} />
                <Upload className="w-5 h-5 mb-1.5" style={{ color: "#00604A" }} />
                <div className="text-sm font-bold" style={{ color: "#00604A" }}>Click to upload · JPG / PNG / WebP</div>
                <div className="text-[10px]" style={{ color: "#667085" }}>Max 2 files · up to 5 MB each</div>
              </label>
              {(aiExtract.files || []).length > 0 && (
                <div className="mt-3 flex gap-2" data-testid="ai-file-thumbs">
                  {aiExtract.files.map((f, i) => (
                    <div key={i} className="relative rounded-xl border overflow-hidden" style={{ borderColor: "#E4E7E5" }}>
                      <img src={URL.createObjectURL(f)} alt="" className="w-24 h-20 object-cover" />
                      <button data-testid={`ai-file-remove-${i}`}
                              onClick={() => removeFile(i)}
                              className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full text-white flex items-center justify-center"
                              style={{ background: "#DC2626" }}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label className="block mt-4">
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>Image URLs (or paste — comma / newline separated)</span>
              <textarea data-testid="ai-image-urls" rows={3}
                        value={aiExtract.image_urls}
                        onChange={(e) => setAiExtract((x) => ({ ...x, image_urls: e.target.value }))}
                        placeholder="https://... , https://..."
                        className="mt-1 w-full border-2 rounded-2xl px-3 py-2 outline-none text-sm"
                        style={{ borderColor: "#E4E7E5" }} />
            </label>

            <label className="block mt-3">
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>Web page URL (FB / Insta / any listing site)</span>
              <input data-testid="ai-page-url"
                     value={aiExtract.page_url}
                     onChange={(e) => setAiExtract((x) => ({ ...x, page_url: e.target.value }))}
                     placeholder="https://www.facebook.com/marketplace/item/…"
                     className="mt-1 w-full border-2 rounded-2xl px-3 py-2 outline-none text-sm"
                     style={{ borderColor: "#E4E7E5" }} />
            </label>

            <label className="block mt-3">
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>Extra context (optional)</span>
              <input data-testid="ai-extra-context"
                     value={aiExtract.extra_context}
                     onChange={(e) => setAiExtract((x) => ({ ...x, extra_context: e.target.value }))}
                     placeholder="Anything to guide extraction — city hint, price note…"
                     className="mt-1 w-full border-2 rounded-2xl px-3 py-2 outline-none text-sm"
                     style={{ borderColor: "#E4E7E5" }} />
            </label>

            <label className="flex items-center gap-2 mt-3 text-sm">
              <input data-testid="ai-publish"
                     type="checkbox" checked={aiExtract.publish}
                     onChange={(e) => setAiExtract((x) => ({ ...x, publish: e.target.checked }))} />
              <span>Publish live immediately (admin default). If extraction misses required fields, the listing stays as a draft <b>and no credit is charged</b>.</span>
            </label>

            <button data-testid="ai-run-extract"
              onClick={runAiExtract} disabled={aiExtract.busy}
              className="mt-4 w-full py-3 rounded-full font-bold text-white shadow-md transition"
              style={{ background: aiExtract.busy ? "#94A3B8" : "#F45118" }}>
              {aiExtract.busy ? "Extracting…" : "Extract & Create Draft"}
            </button>
          </div>

          <div className="rounded-2xl border p-5 bg-white" style={{ borderColor: "#E4E7E5" }}>
            <div className="text-xs uppercase tracking-widest" style={{ color: "#667085" }}>Last extraction</div>
            {!aiExtract.last && <div className="text-sm mt-2" style={{ color: "#667085" }}>Nothing yet. Try pasting a property photo or a Facebook Marketplace link.</div>}
            {aiExtract.last?.listing && (
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="chip">{aiExtract.last.listing.status}</span>
                  <span className="chip text-[10px]">{aiExtract.last.listing.listing_code}</span>
                  {aiExtract.last.credit_charged
                    ? <span className="chip text-[10px]" style={{ color: "#00604A" }}>💳 {aiExtract.last.extracted_fields_count} fields</span>
                    : <span className="chip text-[10px]" style={{ color: "#F45118" }}>🎁 No credit charged</span>}
                  {aiExtract.last.listing.auto_created_user_id && <span className="chip text-[10px]" style={{ color: "#00604A" }}>👤 auto-user created</span>}
                </div>
                <div><b>City:</b> {aiExtract.last.listing.city || "—"} · <b>Locality:</b> {aiExtract.last.listing.locality || "—"}</div>
                <div><b>Type:</b> {aiExtract.last.listing.property_type} · <b>Rent:</b> ₹{aiExtract.last.listing.rent_inr || "—"}</div>
                <div><b>Owner:</b> {aiExtract.last.listing.owner_name || "—"} · <b>Mobile:</b> {aiExtract.last.listing.mobile || "—"}</div>
                {aiExtract.last.listing.description && <div className="text-xs" style={{ color: "#667085" }}>{aiExtract.last.listing.description}</div>}
                {(aiExtract.last.listing.photos || []).length > 0 && (
                  <div className="flex gap-2 mt-2">
                    {aiExtract.last.listing.photos.slice(0, 2).map((u, i) => (
                      <img key={i} src={photoUrl(u)} alt="" className="w-24 h-20 object-cover rounded-xl border" />
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  {aiExtract.last.listing.status !== "active" && (
                    <button data-testid="ai-publish-last"
                            onClick={() => publishListing(aiExtract.last.listing.id)}
                            className="btn-primary text-xs px-3 py-1.5">Publish now</button>
                  )}
                  <button onClick={() => setTab("drafts")} className="chip text-xs">Open drafts →</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "drafts" && (
        <div className="space-y-3" data-testid="drafts-panel">
          {drafts.length === 0 && <div className="rounded-2xl border border-black/10 bg-white p-8 text-center text-sm text-neutral-500">No drafts. Use the AI Auto-Extract tab to create one.</div>}
          {drafts.map((l) => (
            <ListingCardEditor
              key={l.id} listing={l} token={token} api={api}
              editing={editing} setEditing={setEditing}
              onSave={(patch) => patchListing(l.id, patch)}
              onPublish={() => publishListing(l.id)}
              onReject={() => rejectListing(l.id)}
              onDelete={() => deleteListing(l.id)}
              onRefresh={refresh}
              extraActions={null}
            />
          ))}
        </div>
      )}

      {tab === "listings" && (
        <div className="space-y-3" data-testid="listings-panel">
          {listings.length === 0 && <div className="rounded-2xl border border-black/10 bg-white p-8 text-center text-sm text-neutral-500">No listings yet.</div>}
          {listings.map((l) => (
            <ListingCardEditor
              key={l.id} listing={l} token={token} api={api}
              editing={editing} setEditing={setEditing}
              onSave={(patch) => patchListing(l.id, patch)}
              onPublish={() => publishListing(l.id)}
              onReject={() => rejectListing(l.id)}
              onDelete={() => deleteListing(l.id)}
              onRefresh={refresh}
              extraActions={
                <div className="flex gap-1 flex-wrap">
                  <button data-testid={`flag-feat-${l.id}`} onClick={() => flag(l.id, { featured: !l.featured })} className="chip text-[10px]"><Flame className="w-3 h-3" /> {l.featured ? "Un-Feature" : "Feature"}</button>
                  <button data-testid={`flag-trust-${l.id}`} onClick={() => flag(l.id, { trusted: !l.trusted })} className="chip text-[10px]"><Shield className="w-3 h-3" /> {l.trusted ? "Un-Trust" : "Trust"}</button>
                  <button data-testid={`flag-verify-${l.id}`} onClick={() => flag(l.id, { verified_flat: !l.verified_flat })} className="chip text-[10px]"><BadgeCheck className="w-3 h-3" /> {l.verified_flat ? "Un-Verify" : "Verify Flat"}</button>
                  <button onClick={() => unpublishListing(l.id)} className="chip text-[10px]">Unpublish</button>
                </div>
              }
            />
          ))}
        </div>
      )}

      {tab === "free" && (
        <FreeListingSection api={api} settings={settings} refresh={refresh} />
      )}

      {tab === "requests" && (
        <ContactRequestsSection api={api} reqs={contactReqs} refresh={refresh} />
      )}

      {tab === "outreach" && (
        <OwnerOutreachSection outreach={outreach} api={api} refresh={refresh} />
      )}

      {tab === "partners" && (
        <PartnersSection api={api} partners={partners} refresh={refresh} />
      )}

      {tab === "journeys" && (
        <div className="space-y-2">
          {journeys.map((j) => <JourneyRow key={j.id} j={j} api={api} refresh={refresh} listings={listings} />)}
          {journeys.length === 0 && <div className="p-6 text-center text-neutral-500 text-sm">No journeys yet.</div>}
        </div>
      )}

      {tab === "payments" && (
        <div className="rounded-2xl border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-neutral-500 bg-neutral-50">
              <tr><th className="p-3 text-left">Kind</th><th className="p-3">Amount</th><th className="p-3 text-left">UTR</th><th className="p-3">Status</th><th className="p-3">Action</th></tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-t border-black/5">
                  <td className="p-3">
                    <div className="font-bold">{p.kind}</div>
                    <div className="text-[10px] text-neutral-400">{p.pack?.name || p.plan?.name}</div>
                  </td>
                  <td className="p-3 text-center">₹{p.pack?.price_inr || p.plan?.price_inr}</td>
                  <td className="p-3 font-mono text-xs">{p.utr}</td>
                  <td className="p-3 text-center"><span className="chip text-[10px]">{p.status}</span></td>
                  <td className="p-3">
                    {p.status === "pending" && (
                      <div className="flex gap-1 justify-center">
                        <button data-testid={`pay-approve-${p.id}`} onClick={() => payAction(p.id, "approve")} className="btn-primary text-[10px] px-2 py-1 rounded-full text-white inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Approve</button>
                        <button data-testid={`pay-reject-${p.id}`} onClick={() => payAction(p.id, "reject")} className="text-[10px] px-2 py-1 rounded-full border border-red-200 text-red-600 inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-neutral-500 text-sm">No payments yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "verifs" && (
        <div className="space-y-2">
          {verifs.map((v) => (
            <div key={v.id} className="rounded-xl border border-black/10 bg-white p-3 text-sm flex justify-between items-center">
              <div>
                <div className="font-bold">{v.tenant_name} · {v.tenant_mobile}</div>
                <div className="text-xs text-neutral-500">Listing {v.listing_id.slice(0, 10)}… · {new Date(v.created_at).toLocaleString()}</div>
              </div>
              <div className="flex gap-1">
                <span className="chip text-[10px]">{v.status}</span>
                {v.status === "pending" && (
                  <>
                    <button onClick={() => tvMark(v.id, "verified")} className="chip text-[10px] bg-emerald-50 border-emerald-500 text-emerald-700">Verify</button>
                    <button onClick={() => tvMark(v.id, "rejected")} className="chip text-[10px] bg-red-50 border-red-500 text-red-700">Reject</button>
                  </>
                )}
              </div>
            </div>
          ))}
          {verifs.length === 0 && <div className="p-6 text-center text-neutral-500 text-sm">No tenant verification requests.</div>}
        </div>
      )}

      {tab === "users" && (
        <div className="rounded-2xl border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-neutral-500 bg-neutral-50">
              <tr><th className="p-3 text-left">User</th><th className="p-3">Role</th><th className="p-3">Credits</th><th className="p-3">Plan</th><th className="p-3">Verified</th><th className="p-3">Action</th></tr>
            </thead>
            <tbody>
              {profiles.map((u) => (
                <tr key={u.user_id} className="border-t border-black/5">
                  <td className="p-3 font-mono text-xs">{u.user_id.slice(0, 12)}…</td>
                  <td className="p-3 text-center">{u.role || "—"}</td>
                  <td className="p-3 text-center">{u.credits || 0}</td>
                  <td className="p-3 text-center">{u.active_plan_slug || "—"}</td>
                  <td className="p-3 text-center">{u.verified_flatmate ? "✅" : "—"}</td>
                  <td className="p-3 text-center">
                    <button onClick={() => verifyUser(u.user_id, !u.verified_flatmate)} className="chip text-[10px]">
                      {u.verified_flatmate ? "Revoke" : "Grant"}
                    </button>
                  </td>
                </tr>
              ))}
              {profiles.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-neutral-500 text-sm">No flatmate users yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "settings" && settings && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-black/10 bg-white p-4">
            <div className="font-display text-lg mb-3">General</div>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <label className="block">UPI ID
                <input value={settings.upi_id || ""} onChange={(e) => setSettings({ ...settings, upi_id: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 p-2" />
              </label>
              <label className="block">UPI QR image URL
                <input value={settings.upi_qr_url || ""} onChange={(e) => setSettings({ ...settings, upi_qr_url: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 p-2" />
              </label>
              <label className="block">First check price ₹
                <input type="number" value={settings.first_check_price_inr || 0} onChange={(e) => setSettings({ ...settings, first_check_price_inr: parseInt(e.target.value || "0") })} className="mt-1 w-full rounded-xl border border-black/10 p-2" />
              </label>
              <label className="block">Tenant verification price ₹
                <input type="number" value={settings.tenant_verification_price_inr || 0} onChange={(e) => setSettings({ ...settings, tenant_verification_price_inr: parseInt(e.target.value || "0") })} className="mt-1 w-full rounded-xl border border-black/10 p-2" />
              </label>
              <label className="block">Featured slots
                <input type="number" value={settings.featured_slots || 0} onChange={(e) => setSettings({ ...settings, featured_slots: parseInt(e.target.value || "0") })} className="mt-1 w-full rounded-xl border border-black/10 p-2" />
              </label>
              <label className="block">Featured duration (days)
                <input type="number" value={settings.featured_duration_days || 0} onChange={(e) => setSettings({ ...settings, featured_duration_days: parseInt(e.target.value || "0") })} className="mt-1 w-full rounded-xl border border-black/10 p-2" />
              </label>
              <label className="block">Credit validity days
                <input type="number" value={settings.credit_validity_days || 0} onChange={(e) => setSettings({ ...settings, credit_validity_days: parseInt(e.target.value || "0") })} className="mt-1 w-full rounded-xl border border-black/10 p-2" />
              </label>
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox" checked={!!settings.match_display_enabled} onChange={(e) => setSettings({ ...settings, match_display_enabled: e.target.checked })} /> Show match %
              </label>
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox" checked={!!settings.featured_paid} onChange={(e) => setSettings({ ...settings, featured_paid: e.target.checked })} /> Featured is paid
              </label>
            </div>
          </div>

          <details className="rounded-2xl border border-black/10 bg-white p-4">
            <summary className="cursor-pointer font-display text-lg">Plans (JSON edit)</summary>
            <textarea rows="6" className="w-full mt-2 font-mono text-xs rounded-xl border border-black/10 p-3"
                      value={JSON.stringify(settings.plans, null, 2)}
                      onChange={(e) => { try { setSettings({ ...settings, plans: JSON.parse(e.target.value) }); } catch {} }} />
          </details>

          <details className="rounded-2xl border border-black/10 bg-white p-4">
            <summary className="cursor-pointer font-display text-lg">Credit packs (JSON edit)</summary>
            <textarea rows="6" className="w-full mt-2 font-mono text-xs rounded-xl border border-black/10 p-3"
                      value={JSON.stringify(settings.credit_packs, null, 2)}
                      onChange={(e) => { try { setSettings({ ...settings, credit_packs: JSON.parse(e.target.value) }); } catch {} }} />
          </details>

          <details className="rounded-2xl border border-black/10 bg-white p-4">
            <summary className="cursor-pointer font-display text-lg">Action costs (JSON edit)</summary>
            <textarea rows="6" className="w-full mt-2 font-mono text-xs rounded-xl border border-black/10 p-3"
                      value={JSON.stringify(settings.action_costs, null, 2)}
                      onChange={(e) => { try { setSettings({ ...settings, action_costs: JSON.parse(e.target.value) }); } catch {} }} />
          </details>

          <details className="rounded-2xl border border-black/10 bg-white p-4">
            <summary className="cursor-pointer font-display text-lg">Cities (JSON edit)</summary>
            <textarea rows="8" className="w-full mt-2 font-mono text-xs rounded-xl border border-black/10 p-3"
                      value={JSON.stringify(settings.cities, null, 2)}
                      onChange={(e) => { try { setSettings({ ...settings, cities: JSON.parse(e.target.value) }); } catch {} }} />
          </details>

          <details className="rounded-2xl border border-black/10 bg-white p-4">
            <summary className="cursor-pointer font-display text-lg">Lifestyle tags (JSON edit)</summary>
            <textarea rows="8" className="w-full mt-2 font-mono text-xs rounded-xl border border-black/10 p-3"
                      value={JSON.stringify(settings.lifestyle_tags, null, 2)}
                      onChange={(e) => { try { setSettings({ ...settings, lifestyle_tags: JSON.parse(e.target.value) }); } catch {} }} />
          </details>

          <button data-testid="fm-save-settings" onClick={saveSettings} className="btn-primary rounded-full text-white px-6 py-2 inline-flex items-center gap-2">
            <Save className="w-4 h-4" /> Save settings
          </button>
        </div>
      )}
    </div>
  );
}

function ListingCardEditor({ listing: l, token, api, editing, setEditing, onSave, onPublish, onReject, onDelete, onRefresh, extraActions }) {
  const isEdit = editing?.id === l.id;
  const p = isEdit ? { ...l, ...editing.patch } : l;
  const setField = (k, v) => setEditing({ id: l.id, patch: { ...(editing?.patch || {}), [k]: v } });

  const [busyIdx, setBusyIdx] = useState(null);

  const uploadPhoto = async (file, replaceIndex = null) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Only JPG / PNG / WebP allowed");
      return;
    }
    setBusyIdx(replaceIndex ?? "add");
    try {
      const form = new FormData();
      form.append("file", file);
      if (replaceIndex !== null) form.append("replace_index", String(replaceIndex));
      await axios.post(`${API}/admin/flatmate/listings/${l.id}/photos`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      toast.success(replaceIndex !== null ? "Photo replaced" : "Photo added");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    } finally { setBusyIdx(null); }
  };
  const deletePhoto = async (idx) => {
    if (!window.confirm("Delete this image?")) return;
    setBusyIdx(idx);
    try {
      await axios.delete(`${API}/admin/flatmate/listings/${l.id}/photos/${idx}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Image deleted");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusyIdx(null); }
  };

  // Client-side crop-to-square via canvas
  const cropToSquare = async (idx) => {
    const url = photoUrl((p.photos || [])[idx]);
    if (!url) return;
    setBusyIdx(idx);
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
      const side = Math.min(img.naturalWidth, img.naturalHeight);
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = side;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, side, side);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));
      if (!blob) throw new Error("crop failed");
      const file = new File([blob], `cropped-${idx}.jpg`, { type: "image/jpeg" });
      await uploadPhoto(file, idx);
    } catch (e) {
      toast.error("Crop failed — try re-uploading");
      setBusyIdx(null);
    }
  };

  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4" data-testid={`listing-card-${l.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="chip">{l.status}</span>
          <span className="chip text-[10px]">{l.listing_code || l.id.slice(0, 10)}</span>
          <span className="chip text-[10px]">{l.source}</span>
          {l.ai_extracted && <span className="chip text-[10px]" style={{ color: "#F45118" }}>✨ AI</span>}
          {l.credit_charged === false && <span className="chip text-[10px]" style={{ color: "#F45118" }}>🎁 no credit</span>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isEdit && <button data-testid={`edit-${l.id}`} onClick={() => setEditing({ id: l.id, patch: {} })} className="chip text-xs">✎ Edit fields</button>}
          {isEdit && <button data-testid={`save-${l.id}`} onClick={() => onSave(editing.patch)} className="btn-primary text-xs px-3 py-1.5">Save</button>}
          {isEdit && <button onClick={() => setEditing(null)} className="chip text-xs">Cancel</button>}
          {l.status !== "active" && <button data-testid={`publish-${l.id}`} onClick={onPublish} className="chip text-xs" style={{ color: "#00604A" }}>✓ Publish</button>}
          <button onClick={onReject} className="chip text-xs" style={{ color: "#DC2626" }}>Reject</button>
          <button onClick={onDelete} className="chip text-xs" style={{ color: "#DC2626" }}>Delete</button>
        </div>
      </div>

      {extraActions && <div className="mb-3">{extraActions}</div>}
      <FlatmateWaIntents listing={l} token={token} />

      <div className="grid md:grid-cols-3 gap-3 text-sm">
        {[
          ["city", "City"], ["locality", "Locality"], ["property_type", "Type"],
          ["rent_inr", "Rent ₹"], ["deposit_inr", "Deposit ₹"], ["room_type", "Room"],
          ["furnished", "Furnished"], ["available_from", "Available"], ["preferred_tenant", "Prefer"],
          ["owner_name", "Owner"], ["mobile", "Mobile"],
        ].map(([k, label]) => (
          <label key={k} className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
            <input data-testid={`field-${k}-${l.id}`}
                   disabled={!isEdit}
                   value={p[k] ?? ""}
                   onChange={(e) => setField(k, e.target.value)}
                   className="mt-0.5 w-full border rounded-lg px-2 py-1.5 text-sm disabled:bg-neutral-50 disabled:text-neutral-700"
            />
          </label>
        ))}
        <label className="block md:col-span-3">
          <span className="text-[10px] uppercase tracking-widest text-neutral-500">Description</span>
          <textarea disabled={!isEdit}
                    value={p.description ?? ""}
                    onChange={(e) => setField("description", e.target.value)}
                    className="mt-0.5 w-full border rounded-lg px-2 py-1.5 text-sm disabled:bg-neutral-50" rows={2} />
        </label>
        <label className="block md:col-span-3">
          <span className="text-[10px] uppercase tracking-widest text-neutral-500">Amenities (comma separated)</span>
          <input disabled={!isEdit}
                 value={Array.isArray(p.amenities) ? p.amenities.join(", ") : (p.amenities || "")}
                 onChange={(e) => setField("amenities", e.target.value)}
                 className="mt-0.5 w-full border rounded-lg px-2 py-1.5 text-sm disabled:bg-neutral-50" />
        </label>
      </div>

      {/* Photos — inline crop / replace / delete / add — max 2 */}
      <div className="mt-4 flex flex-wrap gap-3 items-start" data-testid={`photos-${l.id}`}>
        {(p.photos || []).map((u, i) => (
          <div key={i} className="relative rounded-xl overflow-hidden border" style={{ borderColor: "#E4E7E5" }}>
            <img src={photoUrl(u)} alt="" className="w-32 h-24 object-cover" />
            <div className="absolute inset-x-0 bottom-0 flex justify-between p-1 bg-black/50 text-white text-[9px]">
              <button data-testid={`photo-crop-${l.id}-${i}`}
                      onClick={() => cropToSquare(i)}
                      disabled={busyIdx === i}
                      className="px-1.5 py-0.5 rounded-full bg-white/20 hover:bg-white/30 font-bold">🌾 Crop</button>
              <label className="px-1.5 py-0.5 rounded-full bg-white/20 hover:bg-white/30 font-bold cursor-pointer">
                ⤴ Replace
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                       onChange={(e) => uploadPhoto(e.target.files?.[0], i)} />
              </label>
              <button data-testid={`photo-delete-${l.id}-${i}`}
                      onClick={() => deletePhoto(i)}
                      disabled={busyIdx === i}
                      className="px-1.5 py-0.5 rounded-full bg-red-600 hover:bg-red-500 font-bold">✕</button>
            </div>
            {busyIdx === i && <div className="absolute inset-0 bg-black/40 grid place-items-center text-white text-xs">…</div>}
          </div>
        ))}
        {(p.photos || []).length < 2 && (
          <label data-testid={`photo-add-${l.id}`} className="w-32 h-24 rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer text-xs font-bold hover:bg-neutral-50"
                 style={{ borderColor: "#00604A55", color: "#00604A" }}>
            <Upload className="w-4 h-4 mb-1" />
            {busyIdx === "add" ? "Uploading…" : "Add photo"}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                   onChange={(e) => uploadPhoto(e.target.files?.[0], null)} />
          </label>
        )}
      </div>
    </div>
  );
}


function JourneyRow({ j, api, refresh, listings }) {
  const [expand, setExpand] = useState(false);
  const [status, setStatus] = useState(j.status);
  const [helper, setHelper] = useState(j.assigned_helper_id || "");
  const [report, setReport] = useState(j.report || {});
  const [verifyLid, setVerifyLid] = useState("");

  const save = async (approve = false) => {
    const patch = { status, assigned_helper_id: helper || null, report };
    if (approve) {
      patch.report_approved = true;
      if (verifyLid) patch.verify_listing_id = verifyLid;
      patch.status = "report_delivered";
    }
    await api.post(`/admin/flatmate/journeys/${j.id}`, patch);
    toast.success("Journey updated");
    refresh();
  };

  return (
    <div className="rounded-xl border border-black/10 bg-white p-3 text-sm">
      <div className="flex justify-between items-center cursor-pointer" onClick={() => setExpand((x) => !x)}>
        <div>
          <div className="font-bold">{j.area}, {j.city} — ₹{j.budget_inr}</div>
          <div className="text-xs text-neutral-500">User {j.user_id.slice(0, 10)}… · UTR {j.utr || "credits"} · {new Date(j.created_at).toLocaleString()}</div>
        </div>
        <span className="chip text-[10px]">{status}</span>
      </div>
      {expand && (
        <div className="mt-3 space-y-2">
          <div className="grid md:grid-cols-2 gap-2">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-black/10 p-2 text-xs">
              {["pending_payment", "payment_confirmed", "helper_assigned", "on_site_visit", "report_pending", "report_delivered", "closed"].map((s) => <option key={s}>{s}</option>)}
            </select>
            <input placeholder="Assigned helper user id" value={helper} onChange={(e) => setHelper(e.target.value)} className="rounded-xl border border-black/10 p-2 text-xs" />
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            {["overall_rating", "document_verification", "real_condition", "issues_found",
              "owner_details", "agreement_terms", "hidden_factors", "maid", "parking",
              "locality_notes", "extra_options", "helper_recommendation"].map((k) => (
              <textarea key={k} placeholder={k.replace(/_/g, " ")} rows="2"
                        value={report[k] || ""} onChange={(e) => setReport({ ...report, [k]: e.target.value })}
                        className="rounded-xl border border-black/10 p-2 text-xs" />
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={verifyLid} onChange={(e) => setVerifyLid(e.target.value)} className="rounded-xl border border-black/10 p-2 text-xs">
              <option value="">(Optional) Mark listing as Verified Flat…</option>
              {listings.map((l) => <option key={l.id} value={l.id}>{l.locality}, {l.city} · ₹{l.rent_inr}</option>)}
            </select>
            <button onClick={() => save(false)} className="chip text-[10px]"><Save className="w-3 h-3" /> Save</button>
            <button onClick={() => save(true)} className="btn-primary text-[10px] px-3 py-1 rounded-full text-white">Approve & Deliver</button>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- Free Listing (individual + Excel bulk) --------------------
function FreeListingSection({ api, settings, refresh }) {
  const [form, setForm] = useState({
    city: "", property_type: "flat", locality: "", rent_inr: "", deposit_inr: "",
    room_type: "", furnished: "", available_from: "", owner_name: "", mobile: "",
    description: "", great_deal: false, photos: "",
  });
  const [busy, setBusy] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkResult, setBulkResult] = useState(null);

  const submitOne = async (e) => {
    e.preventDefault();
    if (!form.city) return toast.error("City is required");
    setBusy(true);
    try {
      const payload = { ...form };
      ["rent_inr", "deposit_inr"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
        else payload[k] = parseInt(payload[k]);
      });
      ["locality", "room_type", "furnished", "available_from", "owner_name",
        "mobile", "description"].forEach((k) => { if (!payload[k]) delete payload[k]; });
      payload.photos = payload.photos ? payload.photos.split(",").map((s) => s.trim()).filter(Boolean) : [];
      await api.post("/admin/flatmate/listings/free", payload);
      toast.success("Free listing published");
      setForm({ ...form, locality: "", rent_inr: "", deposit_inr: "", owner_name: "", mobile: "", description: "" });
      refresh();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const runBulk = async () => {
    if (!bulkText.trim()) return toast.error("Paste CSV rows first");
    const lines = bulkText.trim().split(/\r?\n/);
    if (lines.length < 2) return toast.error("Need at least 1 header row + 1 data row");
    const headers = lines[0].split(",").map((h) => h.trim());
    const rows = lines.slice(1).map((line) => {
      const cells = line.split(",").map((c) => c.trim());
      const obj = {};
      headers.forEach((h, i) => { if (cells[i] !== undefined && cells[i] !== "") obj[h] = cells[i]; });
      return obj;
    });
    setBusy(true);
    try {
      const r = await api.post("/admin/flatmate/listings/bulk", { rows });
      setBulkResult(r.data);
      toast.success(`Bulk uploaded — ${r.data.created} created`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="fm-free-section">
      <div className="rounded-2xl border border-black/10 bg-white p-4">
        <div className="font-display text-lg mb-1">Post a free listing (admin)</div>
        <p className="text-xs text-neutral-500 mb-3">Only <b>City</b> is required. Missing fields render as <b>Details on Request</b>.</p>
        <form onSubmit={submitOne} className="grid md:grid-cols-3 gap-2 text-sm">
          <select required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-xl border border-black/10 p-2">
            <option value="">City *</option>
            {(settings?.cities || []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="rounded-xl border border-black/10 p-2">
            <option value="flat">Flat</option><option value="room">Room</option><option value="pg">PG</option><option value="studio">Studio</option>
          </select>
          <input placeholder="Locality" value={form.locality} onChange={(e) => setForm({ ...form, locality: e.target.value })} className="rounded-xl border border-black/10 p-2" />
          <input placeholder="Rent ₹" type="number" value={form.rent_inr} onChange={(e) => setForm({ ...form, rent_inr: e.target.value })} className="rounded-xl border border-black/10 p-2" />
          <input placeholder="Deposit ₹" type="number" value={form.deposit_inr} onChange={(e) => setForm({ ...form, deposit_inr: e.target.value })} className="rounded-xl border border-black/10 p-2" />
          <select value={form.room_type} onChange={(e) => setForm({ ...form, room_type: e.target.value })} className="rounded-xl border border-black/10 p-2">
            <option value="">Room type</option><option value="single">Single</option><option value="shared">Shared</option><option value="entire_flat">Entire flat</option>
          </select>
          <select value={form.furnished} onChange={(e) => setForm({ ...form, furnished: e.target.value })} className="rounded-xl border border-black/10 p-2">
            <option value="">Furnished</option><option value="fully">Fully</option><option value="semi">Semi</option><option value="unfurnished">Unfurnished</option>
          </select>
          <input type="date" value={form.available_from} onChange={(e) => setForm({ ...form, available_from: e.target.value })} className="rounded-xl border border-black/10 p-2" />
          <input placeholder="Owner name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="rounded-xl border border-black/10 p-2" />
          <input placeholder="Owner mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="rounded-xl border border-black/10 p-2" />
          <input placeholder="Photo URLs (comma-separated)" value={form.photos} onChange={(e) => setForm({ ...form, photos: e.target.value })} className="rounded-xl border border-black/10 p-2 md:col-span-2" />
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl border border-black/10 p-2 md:col-span-3" rows="2" />
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.great_deal} onChange={(e) => setForm({ ...form, great_deal: e.target.checked })} /> Mark as Great Deal
          </label>
          <div />
          <button data-testid="submit-free-listing" type="submit" disabled={busy || !form.city}
                  className="btn-primary rounded-full text-white px-4 py-2 disabled:opacity-50">
            {busy ? "Posting…" : "Post free listing"}
          </button>
        </form>
      </div>

      <div className="rounded-2xl border border-black/10 bg-white p-4">
        <div className="font-display text-lg mb-1">Bulk upload — .xlsx / .csv file or paste</div>
        <p className="text-xs text-neutral-500 mb-2">
          Drop an Excel or CSV file (first row = headers). Only <b>city</b> and <b>property_type</b> are required.
          Suggested headers: <code className="text-[10px]">city,property_type,locality,rent_inr,deposit_inr,room_type,furnished,owner_name,mobile,description</code>
        </p>
        <input type="file" accept=".xlsx,.xls,.csv" data-testid="admin-xlsx-file"
               onChange={async (e) => {
                 const f = e.target.files?.[0]; if (!f) return;
                 const buf = await f.arrayBuffer();
                 const wb = XLSX.read(buf, { type: "array" });
                 const sheet = wb.Sheets[wb.SheetNames[0]];
                 const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
                 if (rows.length === 0) return toast.error("Sheet is empty");
                 setBusy(true);
                 try {
                   const r = await api.post("/admin/flatmate/listings/bulk", { rows });
                   setBulkResult(r.data);
                   toast.success(`Uploaded — ${r.data.created} listings from ${f.name}`);
                   refresh();
                 } catch (er) { toast.error(er?.response?.data?.detail || "Failed"); }
                 finally { setBusy(false); e.target.value = ""; }
               }}
               className="block text-sm mb-3" disabled={busy} />
        <div className="border-t pt-3">
          <p className="text-[11px] text-neutral-500 mb-2">…or paste CSV rows below:</p>
          <textarea data-testid="bulk-textarea" value={bulkText} onChange={(e) => setBulkText(e.target.value)}
                    rows="6" placeholder="city,property_type,locality,rent_inr,owner_name,mobile"
                    className="w-full rounded-xl border border-black/10 p-3 font-mono text-xs" />
          <button data-testid="bulk-upload-btn" onClick={runBulk} disabled={busy}
                  className="mt-2 btn-primary rounded-full text-white px-4 py-2 disabled:opacity-50">
            {busy ? "Uploading…" : "Upload CSV paste"}
          </button>
        </div>
        {bulkResult && (
          <div className="mt-3 text-xs">
            <div className="text-emerald-700 font-bold">Created: {bulkResult.created}</div>
            {bulkResult.errors?.length > 0 && (
              <div className="text-red-600 mt-1">
                Errors:
                <ul className="list-disc pl-5">{bulkResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------- Contact Requests (bulk approve) --------------------
function ContactRequestsSection({ api, reqs, refresh }) {
  const [sel, setSel] = useState({});
  const pending = reqs.filter((r) => r.status === "pending");

  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const allChecked = pending.length > 0 && pending.every((r) => sel[r.id]);
  const toggleAll = () => {
    const next = {};
    if (!allChecked) pending.forEach((r) => (next[r.id] = true));
    setSel(next);
  };
  const bulk = async (action) => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (ids.length === 0) return toast.error("Select at least one request");
    await api.post("/admin/flatmate/contact-requests/bulk", { ids, action });
    toast.success(`${action === "approve" ? "Approved" : "Rejected"} ${ids.length} request(s)`);
    setSel({});
    refresh();
  };

  return (
    <div className="space-y-3" data-testid="fm-requests-section">
      <div className="flex justify-between items-center">
        <div>
          <div className="font-display text-lg">Contact requests · admin approval</div>
          <div className="text-xs text-neutral-500">Approve to reveal contact + deduct credits + notify user.</div>
        </div>
        <div className="flex gap-2">
          <button data-testid="bulk-approve" onClick={() => bulk("approve")} className="btn-primary rounded-full text-white text-xs px-4 py-1.5">Bulk approve</button>
          <button data-testid="bulk-reject" onClick={() => bulk("reject")} className="rounded-full border border-red-200 text-red-600 text-xs px-4 py-1.5 font-bold">Bulk reject</button>
        </div>
      </div>
      <div className="rounded-2xl border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-neutral-500 bg-neutral-50">
            <tr>
              <th className="p-3"><input type="checkbox" checked={allChecked} onChange={toggleAll} /></th>
              <th className="p-3 text-left">Requester</th>
              <th className="p-3 text-left">Listing</th>
              <th className="p-3">Cost</th>
              <th className="p-3">Status</th>
              <th className="p-3">When</th>
            </tr>
          </thead>
          <tbody>
            {reqs.map((r) => (
              <tr key={r.id} className="border-t border-black/5">
                <td className="p-3 text-center">
                  {r.status === "pending" && <input type="checkbox" checked={!!sel[r.id]} onChange={() => toggle(r.id)} />}
                </td>
                <td className="p-3">
                  <div className="font-bold">{r.user?.name || "—"}</div>
                  <div className="text-[11px] text-neutral-500">{r.user?.phone || r.user?.email || "—"}</div>
                </td>
                <td className="p-3">
                  <div className="capitalize">{r.listing?.property_type || "—"} · {r.listing?.locality || "—"}, {r.listing?.city || r.listing_city}</div>
                  <div className="text-[10px] text-neutral-400">{r.listing?.source} · {r.listing?.enquiries || 0} enquiries</div>
                </td>
                <td className="p-3 text-center">{r.credit_cost || 2}c</td>
                <td className="p-3 text-center"><span className={`chip text-[10px] ${r.status === "insufficient_credits" ? "bg-red-50 border-red-500 text-red-700" : ""}`}>{r.status}</span></td>
                <td className="p-3 text-[10px] text-neutral-500">
                  {new Date(r.requested_at).toLocaleString()}
                  {r.status === "insufficient_credits" && r.topup_wa_link && (
                    <div className="mt-1">
                      <a href={r.topup_wa_link} target="_blank" rel="noreferrer"
                         data-testid={`topup-wa-${r.id}`}
                         className="inline-flex items-center gap-1 text-emerald-700 font-bold underline">
                        📱 Ping to top-up
                      </a>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {reqs.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-neutral-500 text-sm">No contact requests yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------- Owner Outreach --------------------
function OwnerOutreachSection({ outreach, api, refresh }) {
  return (
    <div className="space-y-3" data-testid="fm-outreach-section">
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4">
        <div className="font-display text-lg">Owner outreach queue</div>
        <div className="text-xs text-neutral-600 mt-1">
          Listings that crossed <b>{outreach.threshold} enquiries</b>. Send them the WhatsApp subscription hook at ₹{outreach.price_inr}/month.
          When they pay, mark them subscribed to auto-transfer the listing to their own account.
        </div>
      </div>
      {outreach.listings.length === 0 && (
        <div className="p-6 text-center text-neutral-500 text-sm">No listings crossed the threshold yet.</div>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        {outreach.listings.map((l) => <OutreachCard key={l.id} l={l} api={api} refresh={refresh} />)}
      </div>
    </div>
  );
}

function OutreachCard({ l, api, refresh }) {
  const [email, setEmail] = useState("");
  const [utr, setUtr] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const subscribe = async () => {
    if (!email) return toast.error("Owner email required");
    setBusy(true);
    try {
      const r = await api.post("/admin/flatmate/outreach/subscribe", { listing_id: l.id, owner_email: email, utr });
      toast.success(`Subscribed. ${r.data.transferred} listing(s) transferred.`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4">
      <div className="flex justify-between items-start gap-2">
        <div>
          <div className="font-bold capitalize">{l.property_type} · {l.locality}, {l.city}</div>
          <div className="text-xs text-neutral-500">Owner: {l.owner_name || "—"} · {l.mobile || "no mobile"}</div>
        </div>
        <span className="chip text-[10px] bg-amber-50 border-amber-500 text-amber-700">{l.enquiries} enquiries</span>
      </div>
      <div className="mt-2 rounded-xl bg-neutral-50 p-3 text-xs whitespace-pre-wrap">{l.wa_message}</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {l.wa_link ? (
          <a href={l.wa_link} target="_blank" rel="noreferrer"
             className="btn-primary rounded-full text-white text-xs px-4 py-1.5 inline-flex items-center gap-1">
            📱 Send WhatsApp
          </a>
        ) : (
          <span className="text-[11px] text-neutral-500">No owner mobile — add one to enable WhatsApp.</span>
        )}
        <button onClick={() => setExpanded((x) => !x)} className="chip text-[10px]">
          {expanded ? "Cancel" : "Mark subscribed →"}
        </button>
      </div>
      {expanded && (
        <div className="mt-3 rounded-xl bg-emerald-50/50 border border-emerald-200 p-3 space-y-2">
          <div className="text-[11px] font-bold text-emerald-800">Transfer listing + activate ₹499/mo plan</div>
          <input placeholder="Owner email *" value={email} onChange={(e) => setEmail(e.target.value)}
                 data-testid={`owner-email-${l.id}`}
                 className="w-full rounded-xl border border-black/10 p-2 text-sm" />
          <input placeholder="UPI UTR (audit, optional)" value={utr} onChange={(e) => setUtr(e.target.value)}
                 className="w-full rounded-xl border border-black/10 p-2 text-sm" />
          <button onClick={subscribe} disabled={busy || !email}
                  data-testid={`owner-subscribe-${l.id}`}
                  className="btn-primary rounded-full text-white text-xs px-4 py-1.5 disabled:opacity-50">
            {busy ? "Transferring…" : "Confirm subscribe & transfer"}
          </button>
          <div className="text-[10px] text-neutral-500">Creates the owner user (if new), activates their ₹499/mo plan, and moves this + any admin listings with the same mobile → owner dashboard.</div>
        </div>
      )}
    </div>
  );
}

// -------------------- Partners --------------------
function PartnersSection({ api, partners, refresh }) {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", partner_code: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/partners", form);
      toast.success("Partner added");
      setForm({ name: "", mobile: "", email: "", partner_code: "", password: "" });
      refresh();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="fm-partners-section">
      <div className="rounded-2xl border border-black/10 bg-white p-4">
        <div className="font-display text-lg mb-2">Add a partner</div>
        <p className="text-xs text-neutral-500 mb-3">Partners get a separate login and can upload listings that route into this admin panel tagged with their partner code.</p>
        <form onSubmit={submit} className="grid md:grid-cols-3 gap-2 text-sm">
          <input required placeholder="Partner name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border border-black/10 p-2" />
          <input required placeholder="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="rounded-xl border border-black/10 p-2" />
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl border border-black/10 p-2" />
          <input required placeholder="Partner code (login username)" value={form.partner_code} onChange={(e) => setForm({ ...form, partner_code: e.target.value })} className="rounded-xl border border-black/10 p-2" />
          <input required type="text" placeholder="Password (min 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl border border-black/10 p-2" />
          <button disabled={busy} className="btn-primary rounded-full text-white px-4 py-2 disabled:opacity-50">{busy ? "Adding…" : "Add partner"}</button>
        </form>
      </div>
      <div className="rounded-2xl border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-neutral-500 bg-neutral-50">
            <tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">Partner code</th><th className="p-3 text-left">Mobile</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Since</th></tr>
          </thead>
          <tbody>
            {partners.map((p) => (
              <tr key={p.id} className="border-t border-black/5">
                <td className="p-3 font-bold">{p.name}</td>
                <td className="p-3 font-mono text-xs">{p.partner_code}</td>
                <td className="p-3">{p.mobile}</td>
                <td className="p-3 text-neutral-500">{p.email}</td>
                <td className="p-3 text-[11px] text-neutral-500">{new Date(p.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {partners.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-neutral-500 text-sm">No partners yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-neutral-500">Partners log in at <a href="/partner" className="text-emerald-700 underline font-bold">/partner</a> with their partner code + password.</div>
    </div>
  );
}

